'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import type { PaymentCompletedData, PaymentFailedData } from '@adyen/adyen-web';
import { SETTLED_DONATION_STATUSES } from '@impact/types/enums';
import { trpc } from '@/lib/trpc';
import type { AdyenCheckoutConfig } from '@/tenants/_shared/components/AdyenCheckout';
import { usePaymentConfig } from '@/tenants/_shared/hooks/usePaymentConfig';
import {
  clearAdyenRedirectSession,
  loadAdyenRedirectSession,
  saveAdyenRedirectSession,
  saveInFlightPayment,
  clearInFlightPayment,
  type StoredAdyenRedirectSession,
} from '@/tenants/_shared/lib/adyenRedirectSession';
import {
  buildCheckoutItemsInput,
  getCheckoutItemsSubtotal,
  getSelectedCheckoutItems,
  selectedCheckoutItemsRequireShipping,
} from '@/tenants/_shared/lib/itemCheckout';
import { useLocale } from 'next-intl';
import { toIsoCountryCode } from '@/tenants/_shared/lib/normalizeCountryCode';

const FINALIZE_TIMEOUT_MS = 30_000;

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  AUD: 'A$',
  CAD: 'C$',
  NZD: 'NZ$',
};

/** Approximate $250 USD equivalent per currency — triggers shipping address collection. */
export const ADDRESS_REQUIRED_THRESHOLDS: Record<string, number> = {
  USD: 250,
  EUR: 230,
  GBP: 200,
  AUD: 400,
  CAD: 350,
  NZD: 425,
};

export type ItemizedCheckoutStep =
  | 'editing'
  | 'creating-session'
  | 'payment'
  | 'voucher-pending'
  | 'finalizing'
  | 'failed';

export interface ItemizedRedirectSession {
  donationId: string;
  sessionId: string;
  sessionData: string;
  redirectResult: string;
}

export interface ItemizedCheckoutSession {
  donationId: string;
  sessionId: string;
  sessionData: string;
}

export interface ItemizedCheckoutAddress {
  streetAddress: string;
  addressLine2?: string;
  city: string;
  state?: string;
  postalCode: string;
  country: string;
}

interface RedirectContext {
  status: string;
  sessionData: string | null;
  sessionId: string | null;
  donationType?: string | null;
}

interface CreateSessionParams {
  donorAddress?: ItemizedCheckoutAddress;
  itemsInput: Array<{ itemId: string; quantity: number }>;
  itemSubtotal: number;
  payableTotal: number;
  paymentCountryCode?: string;
  platformFee?: number;
}

interface BeforeSubmitParams extends CreateSessionParams {
  checkoutSession: ItemizedCheckoutSession;
  paymentMethodType: string;
}

interface PaymentAuthorisedParams {
  donationId: string;
  source: 'checkout' | 'redirect' | 'settled-return';
  storedRedirectSession: StoredAdyenRedirectSession | null;
}

interface UseItemizedCheckoutControllerParams {
  eventId: string;
  selectedItems: Record<string, number>;
  shippingAddress: string;
  shippingAddressLine2?: string;
  shippingCity: string;
  shippingState?: string;
  shippingPostcode: string;
  shippingCountry: string;
  shippingStateRequired?: boolean;
  selectedCurrency: string;
  donorEmail: string | null;
  returnDonationId: string | null;
  redirectResult: string | null;
  returnSessionId: string | null;
  isLocalStorageRecovery?: boolean;
  currentStep?: number;
  completedAt?: string | null;
  flow: StoredAdyenRedirectSession['flow'];
  loadingMessage: string;
  genericErrorMessage: string;
  paymentFailedMessage: string;
  paymentInitFailedMessage: string;
  setErrorMessage: Dispatch<SetStateAction<string | null>>;
  onRedirectReturn?: () => void;
  createSession: (params: CreateSessionParams) => Promise<ItemizedCheckoutSession>;
  buildStoredRedirectSession: (params: {
    session: ItemizedCheckoutSession;
    currencySymbol: string;
    donorAddress?: ItemizedCheckoutAddress;
    itemsInput: Array<{ itemId: string; quantity: number }>;
    itemSubtotal: number;
    payableTotal: number;
    paymentCountryCode?: string;
  }) => Omit<StoredAdyenRedirectSession, 'savedAt'>;
  onPaymentAuthorised: (params: PaymentAuthorisedParams) => Promise<boolean | void>;
  onBeforeSubmit?: (params: BeforeSubmitParams) => Promise<void>;
  shouldHandleRedirectContext?: (redirectContext: RedirectContext) => boolean;
  autoStart?: {
    enabled: boolean;
    debounceMs?: number;
  };
  baseAmount?: number;
  platformFee?: number;
}

function isAcceptedPaymentResult(data: PaymentCompletedData): boolean {
  return (
    data.resultCode === 'Authorised' ||
    data.resultCode === 'Received' ||
    data.resultCode === 'Pending' ||
    data.resultCode === 'PresentToShopper'
  );
}

function buildDonorAddress(params: {
  requiresShipping: boolean;
  shippingAddress: string;
  shippingAddressLine2?: string;
  shippingCity: string;
  shippingState?: string;
  shippingPostcode: string;
  shippingCountry: string;
}): ItemizedCheckoutAddress | undefined {
  if (!params.requiresShipping) {
    return undefined;
  }

  return {
    streetAddress: params.shippingAddress,
    ...(params.shippingAddressLine2 ? { addressLine2: params.shippingAddressLine2 } : {}),
    city: params.shippingCity,
    ...(params.shippingState ? { state: params.shippingState } : {}),
    postalCode: params.shippingPostcode,
    country: params.shippingCountry,
  };
}

export function useItemizedCheckoutController({
  eventId,
  selectedItems,
  shippingAddress,
  shippingAddressLine2,
  shippingCity,
  shippingState,
  shippingPostcode,
  shippingCountry,
  shippingStateRequired,
  selectedCurrency,
  donorEmail,
  returnDonationId,
  redirectResult,
  returnSessionId,
  isLocalStorageRecovery = false,
  currentStep,
  completedAt,
  flow,
  loadingMessage,
  genericErrorMessage,
  paymentFailedMessage,
  paymentInitFailedMessage,
  setErrorMessage,
  onRedirectReturn,
  createSession,
  buildStoredRedirectSession,
  onPaymentAuthorised,
  onBeforeSubmit,
  shouldHandleRedirectContext,
  autoStart,
  baseAmount = 0,
  platformFee,
}: UseItemizedCheckoutControllerParams) {
  const [checkoutSession, setCheckoutSession] = useState<ItemizedCheckoutSession | null>(null);
  const locale = useLocale();
  const [redirectSession, setRedirectSession] = useState<ItemizedRedirectSession | null>(null);
  const [checkoutStep, setCheckoutStep] = useState<ItemizedCheckoutStep>('editing');
  const [checkoutReady, setCheckoutReady] = useState(false);
  const [finalizingTimedOut, setFinalizingTimedOut] = useState(false);

  const storedRedirectSession = useMemo(
    () => (returnSessionId ? loadAdyenRedirectSession(returnSessionId) : null),
    [returnSessionId],
  );
  const isRedirectReturn =
    (Boolean(returnDonationId && redirectResult) ||
      (isLocalStorageRecovery && !!returnDonationId)) &&
    (!storedRedirectSession?.flow || storedRedirectSession.flow === flow);

  const { data: checkoutCatalog, isLoading: isCatalogLoading } =
    trpc.publicPayment.getCheckoutCatalog.useQuery({ eventId }, { enabled: !!eventId });
  const {
    data: paymentConfigData,
    isLoading: isPaymentConfigLoading,
    isFetching: isPaymentConfigFetching,
    isError: isPaymentConfigError,
  } = usePaymentConfig(eventId || undefined);
  const { data: redirectContext, isError: isRedirectContextError } =
    trpc.publicDonation.getRedirectContext.useQuery(
      { donationId: returnDonationId! },
      { enabled: isRedirectReturn },
    );
  const markRedirectFailed = trpc.publicDonation.markRedirectFailed.useMutation();

  const createSessionRef = useRef(createSession);
  const beforeSubmitRef = useRef(onBeforeSubmit);
  const paymentAuthorisedRef = useRef(onPaymentAuthorised);
  const redirectProcessedRef = useRef(false);

  useEffect(() => {
    createSessionRef.current = createSession;
  }, [createSession]);

  useEffect(() => {
    beforeSubmitRef.current = onBeforeSubmit;
  }, [onBeforeSubmit]);

  useEffect(() => {
    paymentAuthorisedRef.current = onPaymentAuthorised;
  }, [onPaymentAuthorised]);

  const catalogItems = useMemo(() => checkoutCatalog?.items ?? [], [checkoutCatalog?.items]);
  const selectedCatalogItems = useMemo(
    () => getSelectedCheckoutItems(catalogItems, selectedItems),
    [catalogItems, selectedItems],
  );
  const itemSubtotal = getCheckoutItemsSubtotal(catalogItems, selectedItems);
  const orderTotal = baseAmount + itemSubtotal;
  const itemsInput = useMemo(
    () => buildCheckoutItemsInput(catalogItems, selectedItems),
    [catalogItems, selectedItems],
  );
  const itemsRequireShipping = selectedCheckoutItemsRequireShipping(catalogItems, selectedItems);
  const currencyForThreshold = checkoutCatalog?.currencyCode ?? selectedCurrency;
  const addressThreshold = ADDRESS_REQUIRED_THRESHOLDS[currencyForThreshold];
  const requiresShipping =
    itemsRequireShipping || (addressThreshold != null && orderTotal > addressThreshold);
  const donorAddress = buildDonorAddress({
    requiresShipping,
    shippingAddress,
    shippingAddressLine2,
    shippingCity,
    shippingState,
    shippingPostcode,
    shippingCountry,
  });
  const paymentConfig = paymentConfigData?.paymentConfigs?.[0];
  const checkoutConfig = useMemo<AdyenCheckoutConfig | null>(
    () =>
      paymentConfig
        ? {
            locale,
            clientKey: paymentConfig.adyenClientKey || '',
            countryCode: toIsoCountryCode(paymentConfigData?.countryCode) || 'US',
            adyenLive: paymentConfig.adyenLive ?? false,
            applePay: paymentConfig.adyenApplePay ?? false,
            googlePay: paymentConfig.adyenGooglePay ?? false,
            ideal: paymentConfig.adyenIdeal ?? false,
            bancontact: paymentConfig.adyenBancontact ?? false,
            sepa: paymentConfig.adyenSepa ?? false,
            twint: paymentConfig.adyenTwint ?? false,
            eps: paymentConfig.adyenEps ?? false,
            multibanco: paymentConfig.adyenMultibanco ?? false,
            paypal: paymentConfig.adyenPaypal ?? false,
          }
        : null,
    [locale, paymentConfig, paymentConfigData?.countryCode],
  );
  const checkoutCurrencyCode =
    checkoutCatalog?.currencyCode ?? paymentConfig?.currencyCode ?? selectedCurrency;
  const currencySymbol =
    paymentConfig?.currencySymbol ??
    CURRENCY_SYMBOLS[checkoutCurrencyCode] ??
    CURRENCY_SYMBOLS[selectedCurrency] ??
    '$';
  const paymentCountryCode =
    toIsoCountryCode(shippingCountry) ||
    toIsoCountryCode(paymentConfigData?.countryCode) ||
    undefined;

  const checkoutFingerprint = JSON.stringify({
    eventId,
    itemsInput,
    shippingAddress,
    shippingAddressLine2,
    shippingCity,
    shippingState,
    shippingPostcode,
    shippingCountry,
    platformFee,
  });
  const checkoutFingerprintRef = useRef(checkoutFingerprint);
  const isCheckoutLocked =
    checkoutStep === 'creating-session' ||
    checkoutStep === 'payment' ||
    checkoutStep === 'voucher-pending' ||
    checkoutStep === 'finalizing';

  const resetCheckout = useCallback(
    (options?: { clearError?: boolean }) => {
      clearInFlightPayment();
      setCheckoutSession(null);
      setRedirectSession(null);
      setCheckoutReady(false);
      setFinalizingTimedOut(false);
      setCheckoutStep('editing');
      if (options?.clearError ?? true) {
        setErrorMessage(null);
      }
    },
    [setErrorMessage],
  );

  useEffect(() => {
    if (currentStep === undefined) {
      return;
    }

    if (currentStep === 4 || completedAt) {
      return;
    }

    queueMicrotask(() => {
      resetCheckout({ clearError: false });
    });
  }, [completedAt, currentStep, resetCheckout]);

  const runPaymentAuthorised = useCallback(
    async (params: {
      donationId: string;
      source: PaymentAuthorisedParams['source'];
      sessionIdToClear?: string | null;
    }) => {
      clearInFlightPayment();
      setCheckoutStep('finalizing');

      const completed = await paymentAuthorisedRef.current({
        donationId: params.donationId,
        source: params.source,
        storedRedirectSession,
      });

      if (completed === false) {
        setCheckoutStep('failed');
        return false;
      }

      if (params.sessionIdToClear) {
        clearAdyenRedirectSession(params.sessionIdToClear);
      }

      return true;
    },
    [storedRedirectSession],
  );

  const handleStartCheckout = useCallback(async () => {
    if (!checkoutConfig || !paymentConfigData?.paymentConfigs?.length) {
      setErrorMessage(
        isPaymentConfigLoading || isPaymentConfigFetching ? loadingMessage : genericErrorMessage,
      );
      return;
    }

    if (!donorEmail) {
      setErrorMessage(genericErrorMessage);
      return;
    }

    if (
      requiresShipping &&
      (!shippingAddress ||
        !shippingCity ||
        !shippingPostcode ||
        !shippingCountry ||
        (shippingStateRequired && !shippingState))
    ) {
      setErrorMessage(genericErrorMessage);
      return;
    }

    setErrorMessage(null);
    setCheckoutStep('creating-session');

    try {
      const session = await createSessionRef.current({
        donorAddress,
        itemsInput,
        itemSubtotal,
        payableTotal: orderTotal,
        paymentCountryCode,
        platformFee,
      });

      saveAdyenRedirectSession(
        buildStoredRedirectSession({
          session,
          currencySymbol,
          donorAddress,
          itemsInput,
          itemSubtotal,
          payableTotal: orderTotal,
          paymentCountryCode,
        }),
      );

      saveInFlightPayment(session.donationId, session.sessionId);

      checkoutFingerprintRef.current = checkoutFingerprint;
      setCheckoutReady(false);
      setCheckoutSession(session);
      setCheckoutStep('payment');
    } catch (error) {
      resetCheckout({ clearError: false });
      setErrorMessage(error instanceof Error ? error.message : genericErrorMessage);
    }
  }, [
    buildStoredRedirectSession,
    checkoutConfig,
    checkoutFingerprint,
    createSessionRef,
    currencySymbol,
    donorAddress,
    donorEmail,
    genericErrorMessage,
    isPaymentConfigFetching,
    isPaymentConfigLoading,
    itemSubtotal,
    itemsInput,
    loadingMessage,
    orderTotal,
    paymentConfigData?.paymentConfigs?.length,
    paymentCountryCode,
    platformFee,
    requiresShipping,
    resetCheckout,
    setErrorMessage,
    shippingAddress,
    shippingCity,
    shippingCountry,
    shippingPostcode,
    shippingState,
    shippingStateRequired,
  ]);

  useEffect(() => {
    if (!autoStart?.enabled) {
      return;
    }

    if (
      requiresShipping ||
      orderTotal <= 0 ||
      checkoutStep !== 'editing' ||
      !checkoutConfig ||
      isPaymentConfigLoading ||
      isPaymentConfigFetching
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      void handleStartCheckout();
    }, autoStart.debounceMs ?? 0);

    return () => window.clearTimeout(timer);
  }, [
    autoStart?.debounceMs,
    autoStart?.enabled,
    checkoutConfig,
    checkoutStep,
    handleStartCheckout,
    isPaymentConfigFetching,
    isPaymentConfigLoading,
    orderTotal,
    requiresShipping,
  ]);

  useEffect(() => {
    if (!checkoutSession) {
      checkoutFingerprintRef.current = checkoutFingerprint;
      return;
    }

    if (checkoutFingerprintRef.current === checkoutFingerprint) {
      return;
    }

    queueMicrotask(() => {
      resetCheckout();
    });
  }, [checkoutFingerprint, checkoutSession, resetCheckout]);

  const handleBeforeSubmit = useCallback(
    async (paymentMethodType: string) => {
      if (!beforeSubmitRef.current || !checkoutSession) {
        return;
      }

      await beforeSubmitRef.current({
        checkoutSession,
        donorAddress,
        itemsInput,
        itemSubtotal,
        payableTotal: orderTotal,
        paymentCountryCode,
        paymentMethodType,
        platformFee,
      });
    },
    [
      checkoutSession,
      donorAddress,
      itemSubtotal,
      itemsInput,
      orderTotal,
      paymentCountryCode,
      platformFee,
    ],
  );

  const handlePaymentFailed = useCallback(
    async (data: PaymentFailedData | undefined, donationId?: string | null) => {
      if (donationId) {
        await markRedirectFailed.mutateAsync({
          donationId,
          reason: data?.resultCode ? `Checkout failed: ${data.resultCode}` : 'Checkout failed',
        });
      }

      setErrorMessage(paymentFailedMessage);
      resetCheckout({ clearError: false });
      setCheckoutStep('failed');
    },
    [markRedirectFailed, paymentFailedMessage, resetCheckout, setErrorMessage],
  );

  const handleCheckoutPaymentCompleted = useCallback(
    (data: PaymentCompletedData) => {
      if (!checkoutSession) {
        return;
      }

      // Voucher payments (Multibanco): keep Drop-in mounted so customer sees payment reference
      if (data.resultCode === 'PresentToShopper') {
        setCheckoutStep('voucher-pending');
        return;
      }

      if (!isAcceptedPaymentResult(data)) {
        setErrorMessage(paymentFailedMessage);
        resetCheckout({ clearError: false });
        setCheckoutStep('failed');
        return;
      }

      void runPaymentAuthorised({
        donationId: checkoutSession.donationId,
        source: 'checkout',
        sessionIdToClear: checkoutSession.sessionId,
      });
    },
    [checkoutSession, paymentFailedMessage, resetCheckout, runPaymentAuthorised, setErrorMessage],
  );

  const handleCheckoutPaymentFailed = useCallback(
    (data: PaymentFailedData | undefined) => {
      void handlePaymentFailed(data, checkoutSession?.donationId);
    },
    [checkoutSession?.donationId, handlePaymentFailed],
  );

  const handleRedirectPaymentCompleted = useCallback(
    (data: PaymentCompletedData) => {
      if (!redirectSession) {
        return;
      }

      if (!isAcceptedPaymentResult(data)) {
        void handlePaymentFailed(undefined, redirectSession.donationId);
        return;
      }

      void runPaymentAuthorised({
        donationId: redirectSession.donationId,
        source: 'redirect',
        sessionIdToClear: returnSessionId ?? redirectSession.sessionId,
      });
    },
    [handlePaymentFailed, redirectSession, returnSessionId, runPaymentAuthorised],
  );

  const handleRedirectPaymentFailed = useCallback(
    (data: PaymentFailedData | undefined) => {
      void handlePaymentFailed(data, redirectSession?.donationId);
    },
    [handlePaymentFailed, redirectSession?.donationId],
  );

  const handleCheckoutReady = useCallback(() => {
    setCheckoutReady(true);
  }, []);

  const handleKeepWaiting = useCallback(() => {
    setFinalizingTimedOut(false);
  }, []);

  const handleCheckoutError = useCallback(() => {
    resetCheckout({ clearError: false });
    setCheckoutStep('failed');
    setErrorMessage(paymentInitFailedMessage);
  }, [paymentInitFailedMessage, resetCheckout, setErrorMessage]);

  useEffect(() => {
    if (!isRedirectReturn) {
      queueMicrotask(() => {
        setRedirectSession(null);
      });
      redirectProcessedRef.current = false;
      return;
    }

    onRedirectReturn?.();
    queueMicrotask(() => {
      setCheckoutStep('finalizing');
    });
  }, [isRedirectReturn, onRedirectReturn]);

  useEffect(() => {
    if (!isRedirectReturn || !isRedirectContextError || checkoutStep === 'failed') {
      return;
    }

    queueMicrotask(() => {
      setErrorMessage(genericErrorMessage);
      resetCheckout({ clearError: false });
      setCheckoutStep('failed');
    });
  }, [
    checkoutStep,
    genericErrorMessage,
    isRedirectContextError,
    isRedirectReturn,
    resetCheckout,
    setErrorMessage,
  ]);

  useEffect(() => {
    if (
      !isRedirectReturn ||
      !returnDonationId ||
      !redirectResult ||
      !redirectContext ||
      redirectSession ||
      redirectProcessedRef.current
    ) {
      return;
    }

    if (shouldHandleRedirectContext && !shouldHandleRedirectContext(redirectContext)) {
      return;
    }

    redirectProcessedRef.current = true;

    if ((SETTLED_DONATION_STATUSES as readonly string[]).includes(redirectContext.status)) {
      if (redirectContext.status === 'paid') {
        queueMicrotask(() => {
          void runPaymentAuthorised({
            donationId: returnDonationId,
            source: 'settled-return',
            sessionIdToClear: returnSessionId,
          });
        });
        return;
      }

      queueMicrotask(() => {
        setErrorMessage(paymentFailedMessage);
        resetCheckout({ clearError: false });
        setCheckoutStep('failed');
      });
      return;
    }

    const sessionData = storedRedirectSession?.sessionData || redirectContext.sessionData;
    const sessionId = returnSessionId || redirectContext.sessionId;

    if (!sessionId || !sessionData) {
      queueMicrotask(() => {
        setErrorMessage(paymentFailedMessage);
        resetCheckout({ clearError: false });
        setCheckoutStep('failed');
      });
      return;
    }

    queueMicrotask(() => {
      setRedirectSession({
        donationId: returnDonationId,
        sessionId,
        sessionData,
        redirectResult,
      });
      setCheckoutStep('finalizing');
    });
  }, [
    isRedirectReturn,
    paymentFailedMessage,
    redirectContext,
    redirectResult,
    redirectSession,
    resetCheckout,
    returnDonationId,
    returnSessionId,
    runPaymentAuthorised,
    setErrorMessage,
    shouldHandleRedirectContext,
    storedRedirectSession?.sessionData,
  ]);

  // localStorage recovery — native app 3DS return with no URL params.
  // The main resolution (above) requires redirectResult. This effect checks
  // redirectContext directly for settlement status.
  const localStorageRecoveryRef = useRef(false);
  useEffect(() => {
    if (!isLocalStorageRecovery || !returnDonationId || !redirectContext) return;
    if (localStorageRecoveryRef.current || redirectProcessedRef.current) return;
    if (shouldHandleRedirectContext && !shouldHandleRedirectContext(redirectContext)) return;
    localStorageRecoveryRef.current = true;
    redirectProcessedRef.current = true;

    if ((SETTLED_DONATION_STATUSES as readonly string[]).includes(redirectContext.status)) {
      if (redirectContext.status === 'paid') {
        queueMicrotask(() => {
          void runPaymentAuthorised({
            donationId: returnDonationId,
            source: 'settled-return',
            sessionIdToClear: returnSessionId,
          });
        });
      } else {
        queueMicrotask(() => {
          setErrorMessage(paymentFailedMessage);
          resetCheckout({ clearError: false });
          setCheckoutStep('failed');
        });
      }
      return;
    }

    // Still pending — no redirectResult available to finalize via SDK, and this
    // controller doesn't have settlement polling. Show finalizing step and let
    // the 30s timeout handle it. The webhook will likely settle soon.
    queueMicrotask(() => {
      setCheckoutStep('finalizing');
    });
  }, [
    isLocalStorageRecovery,
    returnDonationId,
    redirectContext,
    returnSessionId,
    runPaymentAuthorised,
    paymentFailedMessage,
    resetCheckout,
    setErrorMessage,
    shouldHandleRedirectContext,
  ]);

  useEffect(() => {
    if (checkoutStep !== 'finalizing') {
      queueMicrotask(() => setFinalizingTimedOut(false));
      return;
    }

    const timer = window.setTimeout(() => setFinalizingTimedOut(true), FINALIZE_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [checkoutStep]);

  return {
    catalogItems,
    checkoutConfig,
    checkoutCurrencyCode,
    checkoutReady,
    checkoutSession,
    checkoutStep,
    currencySymbol,
    finalizingTimedOut,
    handleBeforeSubmit,
    handleCheckoutError,
    handleCheckoutPaymentCompleted,
    handleCheckoutPaymentFailed,
    handleCheckoutReady,
    handleKeepWaiting,
    handleRedirectPaymentCompleted,
    handleRedirectPaymentFailed,
    handleStartCheckout,
    isCatalogLoading,
    isCheckoutLocked,
    isPaymentConfigError,
    isPaymentConfigFetching,
    isPaymentConfigLoading,
    itemSubtotal,
    itemsInput,
    orderTotal,
    paymentCountryCode,
    redirectSession,
    requiresShipping,
    resetCheckout,
    selectedCatalogItems,
    storedRedirectSession,
  };
}
